// Shared source of truth for Chart-of-Accounts field definitions, used by the
// full-page AccountForm (Daftar Akun) and the QuickAddAccount modal (Jurnal/Biaya)
// so both stay identical in fields, options, structure and validation.

export const CATEGORY_OPTIONS = [
  { v: "Cash & Bank", l: "Kas & Bank", base: "1-10001" },
  { v: "Accounts Receivable (A/R)", l: "Akun Piutang (A/R)", base: "1-10100" },
  { v: "Inventory", l: "Persediaan", base: "1-10200" },
  { v: "Other Current Assets", l: "Aktiva Lancar Lainnya", base: "1-10300" },
  { v: "Fixed Assets", l: "Aktiva Tetap", base: "1-10700" },
  { v: "Depreciation & Amortization", l: "Depresiasi & Amortisasi", base: "1-10751" },
  { v: "Other Assets", l: "Aktiva Lainnya", base: "1-10800" },
  { v: "Accounts Payable (A/P)", l: "Akun Hutang (A/P)", base: "2-20100" },
  { v: "Other Current Liabilities", l: "Kewajiban Lancar Lainnya", base: "2-20200" },
  { v: "Long Term Liabilities", l: "Kewajiban Jangka Panjang", base: "2-20700" },
  { v: "Equity", l: "Ekuitas", base: "3-30001" },
  { v: "Revenue", l: "Pendapatan", base: "4-40000" },
  { v: "Other Income", l: "Pendapatan Lainnya", base: "7-70000" },
  { v: "Cost of Sales", l: "Beban Pokok Penjualan", base: "5-50000" },
  { v: "Expenses", l: "Beban", base: "6-60001" },
  { v: "Other Expenses", l: "Beban Lainnya", base: "8-80000" },
];

export const catLabel = (v) => CATEGORY_OPTIONS.find((c) => c.v === v)?.l || v;

export function suggestCode(accounts, category) {
  const opt = CATEGORY_OPTIONS.find((c) => c.v === category);
  const codes = accounts.filter((a) => a.category === category && /^\d-\d{5}$/.test(a.code)).map((a) => parseInt(a.code.replace("-", ""), 10));
  if (codes.length === 0) return opt?.base || "";
  const next = String(Math.max(...codes) + 1);
  return `${next[0]}-${next.slice(1)}`;
}

export function initAccountForm(initial, accounts, defaultCategory = "Accounts Receivable (A/R)") {
  if (initial) return { name: initial.name || "", code: initial.code || "", category: initial.category, tax: initial.tax || "", description: initial.description || "", parent_code: initial.parent_code || "" };
  return { name: "", code: suggestCode(accounts, defaultCategory), category: defaultCategory, tax: "", description: "", parent_code: "" };
}

export function accountPayload(form, isSub) {
  return { ...form, parent_code: isSub ? form.parent_code : "" };
}

export function AccountFields({ accounts, form, setForm, isSub, setIsSub, initial }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setCategory = (v) => setForm({ ...form, category: v, code: initial ? form.code : suggestCode(accounts, v), parent_code: "" });
  const parents = accounts.filter((a) => a.category === form.category && a.code !== form.code);
  return (
    <>
      <h2 className="acc-section-title">Informasi akun</h2>
      <div className="acc-form-grid">
        <label>Nama akun <b className="required">*</b><span className="acc-counter">{form.name.length} / 255</span>
          <input data-testid="account-field-name" value={form.name} maxLength={255} disabled={initial?.locked} onChange={(e) => set("name", e.target.value)} />
        </label>
        <label>Kode akun<span className="acc-counter">{form.code.length} / 255</span>
          <input data-testid="account-field-code" value={form.code} maxLength={255} disabled={initial?.locked} onChange={(e) => set("code", e.target.value)} />
        </label>
      </div>
      <label className="acc-field">Kategori akun
        <select data-testid="account-field-category" value={form.category} disabled={initial?.locked} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORY_OPTIONS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
      </label>
      <label className="exp-check acc-sub-check">
        <input type="checkbox" data-testid="account-sub-checkbox" checked={isSub} onChange={(e) => setIsSub(e.target.checked)} />
        Buat akun ini bagian dari akun lain
      </label>
      {isSub && (
        <label className="acc-field">Akun induk
          <select data-testid="account-parent-select" value={form.parent_code} onChange={(e) => set("parent_code", e.target.value)}>
            <option value="">Pilih akun induk...</option>
            {parents.map((a) => <option key={a.code} value={a.code}>{`${a.code} · ${a.name}`}</option>)}
          </select>
        </label>
      )}
      <label className="acc-field">Pajak
        <select data-testid="account-field-tax" value={form.tax} onChange={(e) => set("tax", e.target.value)}>
          <option value="">Pilih pajak</option>
          <option value="PPN 11%">PPN 11%</option>
        </select>
      </label>
      <label className="acc-field">Deskripsi<span className="acc-counter">{form.description.length} / 255</span>
        <textarea data-testid="account-field-description" rows={3} maxLength={255} placeholder="Contoh: Akun untuk piutang karyawan" value={form.description} onChange={(e) => set("description", e.target.value)} />
      </label>
      <h2 className="acc-section-title">Akses akun</h2>
      <p className="acc-access-label">Pengguna yang dapat mengakses</p>
      <div className="acc-radios">
        <label><input type="radio" name="acc-access" defaultChecked /> Semua pengguna</label>
        <label className="disabled"><input type="radio" name="acc-access" disabled /> Sebagian pengguna</label>
        <label className="disabled"><input type="radio" name="acc-access" disabled /> Peran tertentu</label>
      </div>
    </>
  );
}
